import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { useProgress } from './useProgress';
import { getPlayerId } from '../utils';
import { apiFetch, getClientPlatform } from "../apiBase";
import { useI18n } from '../i18n';

// Tipos de recompensas do Battle Pass
export type BattlePassRewardType = 
  | 'chips' 
  | 'crystals' 
  | 'skin' 
  | 'tank' 
  | 'emote' 
  | 'spray' 
  | 'frame' 
  | 'explosion_effect'
  | 'trail_effect'
  | 'xp_boost';

export interface BattlePassReward {
  type: BattlePassRewardType;
  amount: number;
  itemId?: string;
  name: string;
  rarity: 'common' | 'rare' | 'epic' | 'legendary';
}

export interface BattlePassLevel {
  level: number;
  xpRequired: number;
  freeReward?: BattlePassReward;
  premiumReward?: BattlePassReward;
}

export interface BattlePassSeason {
  id: string;
  name: string;
  theme: string;
  startDate: number;
  endDate: number;
  maxLevel: number;
  premiumPrice: number; // Em crystals
  levels: BattlePassLevel[];
}

// =====================================================
// STRIPE SUBSCRIPTION INTEGRATION
// =====================================================

export type SubscriptionTier = string;
export type SubscriptionCurrency = 'brl' | 'usd' | 'jpy';

export interface SubscriptionPlanPrice {
  stripePriceId: string | null;
  currency: SubscriptionCurrency;
  unitAmount: number | null;
  recurringInterval: string | null;
  display: string | null;
  source: 'stripe' | 'fallback' | 'none';
}

export interface SubscriptionPlan {
  tier: SubscriptionTier;
  name: string;
  displayName?: string;
  priceMonthly: number; // Valor aproximado em BRL para compatibilidade
  stripePriceId: string;
  prices?: Partial<Record<SubscriptionCurrency, SubscriptionPlanPrice>>;
  primaryPrice?: SubscriptionPlanPrice | null;
  benefits: {
    dailyCrystals: number;
    dailyChips: number;
    xpMultiplier: number;
    chipMultiplier: number;
    exclusiveSkins: boolean;
    priorityQueue: boolean;
    noAds: boolean;
    battlePassIncluded: boolean;
    extraMissions: number; // Missões extras por dia
  };
}

const DEFAULT_SUBSCRIPTION_PLANS: Record<string, SubscriptionPlan> = {
  free: {
    tier: 'free',
    name: 'Gratuito',
    priceMonthly: 0,
    stripePriceId: '',
    benefits: {
      dailyCrystals: 0,
      dailyChips: 50,
      xpMultiplier: 1.0,
      chipMultiplier: 1.0,
      exclusiveSkins: false,
      priorityQueue: false,
      noAds: false,
      battlePassIncluded: false,
      extraMissions: 0,
    },
  },
  bronze: {
    tier: 'bronze',
    name: 'Bronze Pass',
    priceMonthly: 9.90,
    stripePriceId: 'price_bronze_monthly',
    benefits: {
      // Lei Felca: planos pagos não concedem chips (invariante dinheiro→chips)
      dailyCrystals: 25,
      dailyChips: 0,
      xpMultiplier: 1.1,
      chipMultiplier: 1.1,
      exclusiveSkins: false,
      priorityQueue: false,
      noAds: true,
      battlePassIncluded: true,
      extraMissions: 1,
    },
  },
  silver: {
    tier: 'silver',
    name: 'Silver Pass',
    priceMonthly: 24.90,
    stripePriceId: 'price_silver_monthly',
    benefits: {
      // Lei Felca: planos pagos não concedem chips (invariante dinheiro→chips)
      dailyCrystals: 80,
      dailyChips: 0,
      xpMultiplier: 1.25,
      chipMultiplier: 1.25,
      exclusiveSkins: true,
      priorityQueue: false,
      noAds: true,
      battlePassIncluded: true,
      extraMissions: 2,
    },
  },
  gold: {
    tier: 'gold',
    name: 'Gold Pass',
    priceMonthly: 49.90,
    stripePriceId: 'price_gold_monthly',
    benefits: {
      // Lei Felca: planos pagos não concedem chips (invariante dinheiro→chips)
      dailyCrystals: 160,
      dailyChips: 0,
      xpMultiplier: 1.5,
      chipMultiplier: 1.5,
      exclusiveSkins: true,
      priorityQueue: true,
      noAds: true,
      battlePassIncluded: true,
      extraMissions: 3,
    },
  },
};

export let SUBSCRIPTION_PLANS: Record<string, SubscriptionPlan> = {
  ...DEFAULT_SUBSCRIPTION_PLANS,
};

const DEFAULT_SUBSCRIPTION_ORDER = ['bronze', 'silver', 'gold'];

interface ServerPlanPayload {
  tier?: unknown;
  displayName?: unknown;
  benefits?: Partial<SubscriptionPlan['benefits']> | null;
  primaryPrice?: Partial<SubscriptionPlanPrice> | null;
  prices?: Partial<Record<SubscriptionCurrency, Partial<SubscriptionPlanPrice>>>;
}

const normalizeSubscriptionCurrency = (value: unknown): SubscriptionCurrency | null => {
  if (typeof value !== 'string') return null;
  const normalized = value.trim().toLowerCase();
  if (normalized === 'brl' || normalized === 'usd' || normalized === 'jpy') return normalized;
  return null;
};

const toFinitePositiveNumber = (value: unknown, fallback: number): number => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim()) {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return fallback;
};

const toBoolean = (value: unknown, fallback: boolean): boolean => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') {
    const normalized = value.toLowerCase().trim();
    if (normalized === 'true') return true;
    if (normalized === 'false') return false;
  }
  return fallback;
};

const sanitizePlanBenefits = (
  incoming: unknown,
  fallback: SubscriptionPlan['benefits']
): SubscriptionPlan['benefits'] => {
  if (!incoming || typeof incoming !== 'object') {
    return { ...fallback };
  }
  const raw = incoming as Partial<SubscriptionPlan['benefits']>;
  return {
    dailyCrystals: Math.floor(Math.max(0, toFinitePositiveNumber(raw.dailyCrystals, fallback.dailyCrystals))),
    dailyChips: Math.floor(Math.max(0, toFinitePositiveNumber(raw.dailyChips, fallback.dailyChips))),
    xpMultiplier: Math.max(1, toFinitePositiveNumber(raw.xpMultiplier, fallback.xpMultiplier)),
    chipMultiplier: Math.max(1, toFinitePositiveNumber(raw.chipMultiplier, fallback.chipMultiplier)),
    exclusiveSkins: toBoolean(raw.exclusiveSkins, fallback.exclusiveSkins),
    priorityQueue: toBoolean(raw.priorityQueue, fallback.priorityQueue),
    noAds: toBoolean(raw.noAds, fallback.noAds),
    battlePassIncluded: toBoolean(raw.battlePassIncluded, fallback.battlePassIncluded),
    extraMissions: Math.floor(Math.max(0, toFinitePositiveNumber(raw.extraMissions, fallback.extraMissions))),
  };
};

const toPriceMonthlyApprox = (price: SubscriptionPlanPrice | null | undefined): number => {
  if (!price || price.unitAmount === null) return 0;
  if (price.currency === 'jpy') return price.unitAmount / 100;
  return price.unitAmount / 100;
};

const mergeSubscriptionPlansFromServer = (rawPlans: unknown): SubscriptionPlan[] => {
  if (!Array.isArray(rawPlans)) {
    return DEFAULT_SUBSCRIPTION_ORDER
      .map((tier) => SUBSCRIPTION_PLANS[tier])
      .filter((plan): plan is SubscriptionPlan => Boolean(plan));
  }

  const incomingMap: Record<string, SubscriptionPlan> = {};

  for (const entry of rawPlans) {
    if (!entry || typeof entry !== 'object') continue;
    const payload = entry as ServerPlanPayload;
    const tier = typeof payload.tier === 'string' ? payload.tier.trim().toLowerCase() : '';
    if (!tier || tier === 'free') continue;

    const fallback = SUBSCRIPTION_PLANS[tier] || DEFAULT_SUBSCRIPTION_PLANS[tier] || DEFAULT_SUBSCRIPTION_PLANS.free;
    const displayName = typeof payload.displayName === 'string' && payload.displayName.trim()
      ? payload.displayName.trim()
      : fallback.displayName || fallback.name || tier;

    const prices: Partial<Record<SubscriptionCurrency, SubscriptionPlanPrice>> = {};
    if (payload.prices && typeof payload.prices === 'object') {
      for (const currency of ['brl', 'usd', 'jpy'] as SubscriptionCurrency[]) {
        const source = payload.prices[currency];
        if (!source || typeof source !== 'object') continue;
        const normalizedCurrency = normalizeSubscriptionCurrency(source.currency) || currency;
        const unitAmount = typeof source.unitAmount === 'number' && Number.isFinite(source.unitAmount)
          ? source.unitAmount
          : null;
        prices[currency] = {
          stripePriceId: typeof source.stripePriceId === 'string' && source.stripePriceId ? source.stripePriceId : null,
          currency: normalizedCurrency,
          unitAmount,
          recurringInterval: typeof source.recurringInterval === 'string' ? source.recurringInterval : null,
          display: typeof source.display === 'string' ? source.display : null,
          source: source.source === 'stripe' || source.source === 'fallback' || source.source === 'none'
            ? source.source
            : 'none',
        };
      }
    }

    const primarySource = payload.primaryPrice && typeof payload.primaryPrice === 'object'
      ? payload.primaryPrice
      : null;
    const primaryCurrency = normalizeSubscriptionCurrency(primarySource?.currency) || null;
    const primaryPrice: SubscriptionPlanPrice | null = primarySource ? {
      stripePriceId: typeof primarySource.stripePriceId === 'string' && primarySource.stripePriceId ? primarySource.stripePriceId : null,
      currency: primaryCurrency || 'brl',
      unitAmount: typeof primarySource.unitAmount === 'number' && Number.isFinite(primarySource.unitAmount)
        ? primarySource.unitAmount
        : null,
      recurringInterval: typeof primarySource.recurringInterval === 'string' ? primarySource.recurringInterval : null,
      display: typeof primarySource.display === 'string' ? primarySource.display : null,
      source: primarySource.source === 'stripe' || primarySource.source === 'fallback' || primarySource.source === 'none'
        ? primarySource.source
        : 'none',
    } : null;

    const mergedPlan: SubscriptionPlan = {
      tier,
      name: displayName,
      displayName,
      priceMonthly: toPriceMonthlyApprox(primaryPrice),
      stripePriceId: primaryPrice?.stripePriceId || fallback.stripePriceId || '',
      prices,
      primaryPrice,
      benefits: sanitizePlanBenefits(payload.benefits, fallback.benefits),
    };

    incomingMap[tier] = mergedPlan;
  }

  SUBSCRIPTION_PLANS = {
    ...DEFAULT_SUBSCRIPTION_PLANS,
    ...SUBSCRIPTION_PLANS,
    ...incomingMap,
  };

  return Object.values(incomingMap);
};

// Interface de assinatura ativa
export interface ActiveSubscription {
  oderId: string;
  tier: SubscriptionTier;
  stripeSubscriptionId: string;
  stripeCustomerId: string;
  currentPeriodStart: number;
  currentPeriodEnd: number;
  status: 'active' | 'canceled' | 'past_due' | 'trialing';
  cancelAtPeriodEnd: boolean;
  lastDailyClaimDate: string | null;
  totalCrystalsReceived: number;
  totalChipsReceived: number;
  monthsSubscribed: number;
}

// Missões
export interface BattlePassMission {
  id: string;
  type: 'daily' | 'weekly' | 'seasonal';
  title: string;
  description: string;
  xpReward: number;
  progress: number;
  target: number;
  completed: boolean;
  claimed: boolean;
  expiresAt?: number;
}

// Configuração da temporada atual
const CURRENT_SEASON: BattlePassSeason = {
  id: 'season_1',
  name: 'Guerra Industrial',
  theme: 'industrial',
  startDate: Date.now(),
  endDate: Date.now() + 30 * 24 * 60 * 60 * 1000, // 30 dias
  maxLevel: 50,
  premiumPrice: 600, // 600 crystals = ~R$29,90
  levels: generateSeasonLevels(50),
};

// Gerador de níveis do Battle Pass
function generateSeasonLevels(maxLevel: number): BattlePassLevel[] {
  const levels: BattlePassLevel[] = [];
  
  for (let i = 1; i <= maxLevel; i++) {
    // XP necessário aumenta progressivamente
    const xpRequired = Math.floor(100 * (1 + (i - 1) * 0.15));
    
    const level: BattlePassLevel = {
      level: i,
      xpRequired,
    };
    
    // Recompensas gratuitas a cada 2-3 níveis
    if (i % 2 === 0 || i % 5 === 0) {
      level.freeReward = generateFreeReward(i);
    }
    
    // Recompensas premium em todos os níveis
    level.premiumReward = generatePremiumReward(i);
    
    levels.push(level);
  }
  
  return levels;
}

function generateFreeReward(level: number): BattlePassReward {
  // Recompensas gratuitas são principalmente moedas
  if (level % 10 === 0) {
    return {
      type: 'skin',
      amount: 1,
      itemId: `skin_free_${level}`,
      name: `Skin Básica Nv.${level}`,
      rarity: 'common',
    };
  } else if (level % 5 === 0) {
    return {
      type: 'crystals',
      amount: 10 + Math.floor(level / 5) * 5,
      name: `${10 + Math.floor(level / 5) * 5} Cristais`,
      rarity: 'rare',
    };
  } else {
    return {
      type: 'chips',
      amount: 500 + level * 50,
      name: `${500 + level * 50} Chips`,
      rarity: 'common',
    };
  }
}

function generatePremiumReward(level: number): BattlePassReward {
  // Níveis especiais (10, 25, 50)
  if (level === 50) {
    return {
      type: 'tank',
      amount: 1,
      itemId: 'tank_season_1_legendary',
      name: 'Tanque Industrial Supreme',
      rarity: 'legendary',
    };
  } else if (level === 25) {
    return {
      type: 'skin',
      amount: 1,
      itemId: 'skin_season_1_epic',
      name: 'Skin Forja Dourada',
      rarity: 'epic',
    };
  } else if (level === 10) {
    return {
      type: 'frame',
      amount: 1,
      itemId: 'frame_industrial',
      name: 'Moldura Industrial',
      rarity: 'epic',
    };
  } else if (level % 10 === 0) {
    return {
      type: 'skin',
      amount: 1,
      itemId: `skin_premium_${level}`,
      name: `Skin Premium Nv.${level}`,
      rarity: 'epic',
    };
  } else if (level % 5 === 0) {
    return {
      type: level % 15 === 0 ? 'explosion_effect' : 'emote',
      amount: 1,
      itemId: `${level % 15 === 0 ? 'explosion' : 'emote'}_${level}`,
      name: level % 15 === 0 ? 'Explosão Industrial' : `Emote Nv.${level}`,
      rarity: 'rare',
    };
  } else if (level % 3 === 0) {
    return {
      type: 'crystals',
      amount: 15 + Math.floor(level / 3) * 5,
      name: `${15 + Math.floor(level / 3) * 5} Cristais`,
      rarity: 'rare',
    };
  } else {
    return {
      type: 'chips',
      amount: 1000 + level * 100,
      name: `${1000 + level * 100} Chips`,
      rarity: 'common',
    };
  }
}

// Missões diárias template
const DAILY_MISSION_TEMPLATES = [
  { title: 'Vitória Rápida', description: 'Vença 1 partida', target: 1, xp: 50 },
  { title: 'Destruidor', description: 'Destrua 10 inimigos', target: 10, xp: 75 },
  { title: 'Colecionador', description: 'Colete 5 power-ups', target: 5, xp: 40 },
  { title: 'Sobrevivente', description: 'Sobreviva 3 ondas sem morrer', target: 3, xp: 60 },
  { title: 'Sniper', description: 'Acerte 20 tiros', target: 20, xp: 45 },
  { title: 'Maratonista', description: 'Jogue 3 partidas', target: 3, xp: 55 },
  { title: 'Comprador', description: 'Compre 3 itens na loja de onda', target: 3, xp: 35 },
  { title: 'Eliminação Dupla', description: 'Elimine 2 inimigos em 5 segundos', target: 1, xp: 80 },
];

// Missões semanais template
const WEEKLY_MISSION_TEMPLATES = [
  { title: 'Campeão Semanal', description: 'Vença 10 partidas', target: 10, xp: 300 },
  { title: 'Destruição em Massa', description: 'Destrua 100 inimigos', target: 100, xp: 400 },
  { title: 'Mestre dos Power-ups', description: 'Use 50 power-ups', target: 50, xp: 250 },
  { title: 'Invicto', description: 'Complete 5 partidas sem morrer', target: 5, xp: 500 },
  { title: 'Veterano', description: 'Chegue na onda 20 em qualquer modo', target: 1, xp: 350 },
  { title: 'Rico', description: 'Acumule 50.000 chips', target: 50000, xp: 275 },
  { title: 'Social', description: 'Jogue 5 partidas multiplayer', target: 5, xp: 325 },
];

interface BattlePassState {
  // Estado da temporada
  currentSeason: BattlePassSeason;
  hasPremium: boolean;
  
  // Progressão
  currentLevel: number;
  currentXP: number;
  totalXPEarned: number;
  
  // Recompensas coletadas
  claimedFreeRewards: number[];
  claimedPremiumRewards: number[];
  
  // Missões
  dailyMissions: BattlePassMission[];
  weeklyMissions: BattlePassMission[];
  lastDailyRefresh: string | null;
  lastWeeklyRefresh: string | null;
  
  // Estatísticas
  totalMissionsCompleted: number;
  currentSeasonStartDate: number;
  
  // Server sync state
  isLoading: boolean;
  error: string | null;
  
  // Ações
  loadFromServer: () => Promise<void>;
  addXP: (amount: number) => Promise<{ leveledUp: boolean; newLevel?: number }>;
  claimReward: (level: number, isPremium: boolean) => Promise<BattlePassReward | null>;
  purchasePremium: () => boolean;
  activatePremiumFromSubscription: () => void; // Ativa premium via assinatura (sem custo)
  purchaseLevels: (count: number) => boolean;
  
  // Missões
  refreshDailyMissions: () => Promise<void>;
  refreshWeeklyMissions: () => Promise<void>;
  updateMissionProgress: (missionId: string, progress: number) => void;
  claimMissionReward: (missionId: string) => Promise<number>;
  
  // Helpers
  getTimeRemaining: () => { days: number; hours: number; minutes: number };
  canClaimReward: (level: number, isPremium: boolean) => boolean;
  getLevelProgress: () => { current: number; required: number; percentage: number };
}

const getDateString = (): string => new Date().toISOString().split('T')[0];
const getWeekString = (): string => {
  const now = new Date();
  const startOfYear = new Date(now.getFullYear(), 0, 1);
  const weekNumber = Math.ceil(((now.getTime() - startOfYear.getTime()) / 86400000 + startOfYear.getDay() + 1) / 7);
  return `${now.getFullYear()}-W${weekNumber}`;
};

const isFiniteNumber = (value: unknown): value is number => (
  typeof value === 'number' && Number.isFinite(value)
);

const toFiniteNumber = (value: unknown): number | null => {
  if (isFiniteNumber(value)) return value;
  if (typeof value === 'string' && value.trim()) {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
};

const BATTLE_PASS_REQUEST_TIMEOUT_MS = 8000;
const BATTLE_PASS_LOADING_GUARD_MS = 9500;
let battlePassSyncNonce = 0;

async function fetchJsonWithTimeout<T>(path: string, timeoutMs = BATTLE_PASS_REQUEST_TIMEOUT_MS): Promise<T> {
  const controller = new AbortController();
  let timeoutId: number | null = null;
  try {
    const networkPromise = (async () => {
      const response = await apiFetch(path, {
        signal: controller.signal,
        cache: 'no-store',
        headers: { 'Cache-Control': 'no-cache' },
      });
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      return await response.json() as T;
    })();

    const timeoutPromise = new Promise<never>((_, reject) => {
      timeoutId = window.setTimeout(() => {
        controller.abort();
        reject(new Error(`Timeout ao carregar ${path}`));
      }, timeoutMs);
    });

    return await Promise.race([networkPromise, timeoutPromise]);
  } catch (error) {
    if (error instanceof Error && error.name === 'AbortError') {
      throw new Error(`Timeout ao carregar ${path}`);
    }
    throw error;
  } finally {
    if (timeoutId !== null) {
      window.clearTimeout(timeoutId);
    }
  }
}

const sanitizeReward = (reward: unknown): BattlePassReward | undefined => {
  if (!reward || typeof reward !== 'object') return undefined;
  const source = reward as Partial<BattlePassReward>;
  if (typeof source.type !== 'string') return undefined;
  if (typeof source.name !== 'string') return undefined;
  if (!isFiniteNumber(source.amount) || source.amount <= 0) return undefined;
  if (!source.rarity || !['common', 'rare', 'epic', 'legendary'].includes(source.rarity)) return undefined;

  return {
    type: source.type as BattlePassRewardType,
    amount: Math.floor(source.amount),
    itemId: typeof source.itemId === 'string' ? source.itemId : undefined,
    name: source.name,
    rarity: source.rarity as BattlePassReward['rarity'],
  };
};

const sanitizeSeason = (season: unknown): BattlePassSeason => {
  const safeSeason = { ...CURRENT_SEASON };
  if (!season || typeof season !== 'object') return safeSeason;

  const input = season as Partial<BattlePassSeason>;
  const maxLevel = isFiniteNumber(input.maxLevel) && input.maxLevel > 0
    ? Math.min(200, Math.floor(input.maxLevel))
    : safeSeason.maxLevel;

  const fallbackLevels = generateSeasonLevels(maxLevel);
  const sourceLevels = Array.isArray(input.levels) ? input.levels : [];
  const levels: BattlePassLevel[] = [];

  for (let index = 0; index < maxLevel; index++) {
    const fallback = fallbackLevels[index];
    const source = sourceLevels[index] as Partial<BattlePassLevel> | undefined;
    if (!source || typeof source !== 'object') {
      levels.push(fallback);
      continue;
    }

    const xpRequired = isFiniteNumber(source.xpRequired) && source.xpRequired > 0
      ? Math.floor(source.xpRequired)
      : fallback.xpRequired;

    levels.push({
      level: index + 1,
      xpRequired,
      freeReward: sanitizeReward(source.freeReward) ?? fallback.freeReward,
      premiumReward: sanitizeReward(source.premiumReward) ?? fallback.premiumReward,
    });
  }

  return {
    id: typeof input.id === 'string' && input.id ? input.id : safeSeason.id,
    name: typeof input.name === 'string' && input.name ? input.name : safeSeason.name,
    theme: typeof input.theme === 'string' && input.theme ? input.theme : safeSeason.theme,
    startDate: isFiniteNumber(input.startDate) ? input.startDate : safeSeason.startDate,
    endDate: isFiniteNumber(input.endDate) ? input.endDate : safeSeason.endDate,
    maxLevel,
    premiumPrice: isFiniteNumber(input.premiumPrice) && input.premiumPrice >= 0
      ? Math.floor(input.premiumPrice)
      : safeSeason.premiumPrice,
    levels,
  };
};

const sanitizeClaimedLevels = (value: unknown, maxLevel: number): number[] => {
  if (!Array.isArray(value)) return [];
  return [...new Set(
    value
      .filter((entry) => isFiniteNumber(entry))
      .map((entry) => Math.floor(entry))
      .filter((entry) => entry >= 1 && entry <= maxLevel)
  )];
};

const sanitizeMissionList = (value: unknown): BattlePassMission[] => {
  if (!Array.isArray(value)) return [];
  return value.filter((mission): mission is BattlePassMission => (
    !!mission && typeof mission === 'object' && typeof (mission as BattlePassMission).id === 'string'
  ));
};

const normalizeBattlePassState = (
  persisted: unknown,
  current: BattlePassState
): BattlePassState => {
  if (!persisted || typeof persisted !== 'object') return current;
  const source = persisted as Partial<BattlePassState>;
  const currentSeason = sanitizeSeason(source.currentSeason);
  const maxLevel = currentSeason.maxLevel;

  const safeLevel = isFiniteNumber(source.currentLevel)
    ? Math.max(1, Math.min(maxLevel, Math.floor(source.currentLevel)))
    : current.currentLevel;

  const levelConfig = currentSeason.levels[safeLevel - 1];
  const maxXPForLevel = safeLevel >= maxLevel ? 0 : Math.max(0, levelConfig?.xpRequired ?? 0);
  const safeXP = isFiniteNumber(source.currentXP)
    ? Math.max(0, Math.min(maxXPForLevel, Math.floor(source.currentXP)))
    : current.currentXP;

  return {
    ...current,
    currentSeason,
    hasPremium: Boolean(source.hasPremium),
    currentLevel: safeLevel,
    currentXP: safeXP,
    totalXPEarned: isFiniteNumber(source.totalXPEarned) && source.totalXPEarned >= 0
      ? Math.floor(source.totalXPEarned)
      : current.totalXPEarned,
    claimedFreeRewards: sanitizeClaimedLevels(source.claimedFreeRewards, maxLevel),
    claimedPremiumRewards: sanitizeClaimedLevels(source.claimedPremiumRewards, maxLevel),
    dailyMissions: sanitizeMissionList(source.dailyMissions),
    weeklyMissions: sanitizeMissionList(source.weeklyMissions),
    totalMissionsCompleted: isFiniteNumber(source.totalMissionsCompleted) && source.totalMissionsCompleted >= 0
      ? Math.floor(source.totalMissionsCompleted)
      : current.totalMissionsCompleted,
    currentSeasonStartDate: isFiniteNumber(source.currentSeasonStartDate)
      ? source.currentSeasonStartDate
      : current.currentSeasonStartDate,
    lastDailyRefresh: typeof source.lastDailyRefresh === 'string' ? source.lastDailyRefresh : null,
    lastWeeklyRefresh: typeof source.lastWeeklyRefresh === 'string' ? source.lastWeeklyRefresh : null,
    isLoading: false, // Don't persist loading state
    error: null, // Don't persist errors
  };
};

export const useBattlePass = create<BattlePassState>()(
  persist(
    (set, get) => ({
      currentSeason: CURRENT_SEASON,
      hasPremium: false,
      currentLevel: 1,
      currentXP: 0,
      totalXPEarned: 0,
      claimedFreeRewards: [],
      claimedPremiumRewards: [],
      dailyMissions: [],
      weeklyMissions: [],
      lastDailyRefresh: null,
      lastWeeklyRefresh: null,
      totalMissionsCompleted: 0,
      currentSeasonStartDate: Date.now(),
      isLoading: false,
      error: null,
      
      loadFromServer: async () => {
        const syncNonce = ++battlePassSyncNonce;
        set({ isLoading: true, error: null });
        const odysId = getPlayerId();
        const loadingGuard = window.setTimeout(() => {
          if (battlePassSyncNonce !== syncNonce) return;
          const state = get();
          if (!state.isLoading) return;
          set({
            isLoading: false,
            error: 'Sincronização do Battle Pass demorou demais. Exibindo dados locais.',
          });
        }, BATTLE_PASS_LOADING_GUARD_MS);
        
        try {
          // Network requests run in parallel and are optional.
          // Any successful payload updates the local state, while failures keep local fallback.
          const [seasonResult, progressResult, missionsResult] = await Promise.allSettled([
            fetchJsonWithTimeout<{ success?: boolean; season?: unknown }>('/api/battle-pass/season'),
            fetchJsonWithTimeout<{ success?: boolean; progress?: any }>(`/api/battle-pass/progress/${odysId}`),
            fetchJsonWithTimeout<{ success?: boolean; dailyMissions?: unknown; weeklyMissions?: unknown; lastDailyRefresh?: string | null; lastWeeklyRefresh?: string | null }>(`/api/battle-pass/missions/${odysId}`),
          ]);

          const current = get();
          let nextSeason = current.currentSeason;
          let nextHasPremium = current.hasPremium;
          let nextLevel = current.currentLevel;
          let nextXP = current.currentXP;
          let nextClaimedFree = current.claimedFreeRewards;
          let nextClaimedPremium = current.claimedPremiumRewards;
          let nextDailyMissions = current.dailyMissions;
          let nextWeeklyMissions = current.weeklyMissions;
          let nextLastDailyRefresh = current.lastDailyRefresh;
          let nextLastWeeklyRefresh = current.lastWeeklyRefresh;

          let seasonSynced = false;
          let progressSynced = false;
          let missionsSynced = false;

          if (seasonResult.status === 'fulfilled') {
            const seasonData = seasonResult.value;
            if (seasonData?.success && seasonData.season) {
              nextSeason = sanitizeSeason(seasonData.season);
              seasonSynced = true;
            }
          }

          if (progressResult.status === 'fulfilled') {
            const progressData = progressResult.value;
            if (progressData?.success && progressData.progress) {
              const progress = progressData.progress as Record<string, unknown>;
              const maxLevel = Math.max(1, nextSeason.maxLevel);
              const rawLevel = toFiniteNumber(progress.currentLevel);
              const safeLevel = rawLevel === null
                ? nextLevel
                : Math.max(1, Math.min(maxLevel, Math.floor(rawLevel)));
              const levelConfig = nextSeason.levels[safeLevel - 1];
              const maxXPForLevel = safeLevel >= maxLevel ? 0 : Math.max(0, levelConfig?.xpRequired ?? 0);
              const rawXP = toFiniteNumber(progress.currentXp);
              const safeXP = rawXP === null
                ? nextXP
                : Math.max(0, Math.min(maxXPForLevel, Math.floor(rawXP)));

              nextHasPremium = Boolean(progress.hasPremium);
              nextLevel = safeLevel;
              nextXP = safeXP;
              // Backend persists a single claimed list; UI keeps both tracks separately.
              nextClaimedFree = sanitizeClaimedLevels(progress.claimedRewards, maxLevel);
              nextClaimedPremium = sanitizeClaimedLevels(progress.claimedRewards, maxLevel);
              progressSynced = true;
            }
          }

          if (missionsResult.status === 'fulfilled') {
            const missionsData = missionsResult.value;
            if (missionsData?.success) {
              nextDailyMissions = sanitizeMissionList(missionsData.dailyMissions);
              nextWeeklyMissions = sanitizeMissionList(missionsData.weeklyMissions);
              nextLastDailyRefresh = typeof missionsData.lastDailyRefresh === 'string' ? missionsData.lastDailyRefresh : null;
              nextLastWeeklyRefresh = typeof missionsData.lastWeeklyRefresh === 'string' ? missionsData.lastWeeklyRefresh : null;
              missionsSynced = true;
            }
          }

          const hasAnySync = seasonSynced || progressSynced || missionsSynced;
          if (battlePassSyncNonce !== syncNonce) return;

          set({
            currentSeason: nextSeason,
            hasPremium: nextHasPremium,
            currentLevel: nextLevel,
            currentXP: nextXP,
            claimedFreeRewards: nextClaimedFree,
            claimedPremiumRewards: nextClaimedPremium,
            dailyMissions: nextDailyMissions,
            weeklyMissions: nextWeeklyMissions,
            lastDailyRefresh: nextLastDailyRefresh,
            lastWeeklyRefresh: nextLastWeeklyRefresh,
            isLoading: false,
            error: hasAnySync ? null : 'Não foi possível sincronizar o Battle Pass. Exibindo dados locais.',
          });
        } catch (error) {
          if (battlePassSyncNonce !== syncNonce) return;
          set({
            error: error instanceof Error ? error.message : 'Erro ao sincronizar Battle Pass',
            isLoading: false,
          });
        } finally {
          window.clearTimeout(loadingGuard);
        }
      },
      
      addXP: async (amount: number) => {
        const state = get();
        const odysId = getPlayerId();
        
        // Optimistic update
        let { currentXP, currentLevel, currentSeason } = state;
        let leveledUp = false;
        let newLevel: number | undefined;
        
        currentXP += amount;
        
        // Verificar level up
        while (currentLevel < currentSeason.maxLevel) {
          const levelConfig = currentSeason.levels[currentLevel - 1];
          if (!levelConfig || !isFiniteNumber(levelConfig.xpRequired) || levelConfig.xpRequired <= 0) {
            break;
          }
          if (currentXP >= levelConfig.xpRequired) {
            currentXP -= levelConfig.xpRequired;
            currentLevel++;
            leveledUp = true;
            newLevel = currentLevel;
          } else {
            break;
          }
        }
        
        // Cap XP no nível máximo
        if (currentLevel >= currentSeason.maxLevel) {
          currentXP = 0;
        }
        
        set({
          currentXP,
          currentLevel,
          totalXPEarned: state.totalXPEarned + amount,
        });
        
        // Sync with server in background
        try {
          await apiFetch('/api/battle-pass/add-xp', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ odysId, xpAmount: amount }),
          });
        } catch (error) {
          console.error('Failed to sync XP with server:', error);
        }
        
        return { leveledUp, newLevel };
      },
      
      claimReward: async (level: number, isPremium: boolean) => {
        const state = get();
        
        if (!state.canClaimReward(level, isPremium)) {
          return null;
        }
        
        const levelConfig = state.currentSeason.levels[level - 1];
        if (!levelConfig) return null;
        const reward = isPremium ? levelConfig.premiumReward : levelConfig.freeReward;
        
        if (!reward) return null;
        
        const odysId = getPlayerId();
        
        try {
          // Call server to claim
          const response = await apiFetch('/api/battle-pass/claim-reward', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ odysId, level, isPremium }),
          });
          
          const data = await response.json();
          
          if (!data.success) {
            throw new Error(data.error || 'Erro ao reclamar recompensa');
          }
          
          // Update local state after successful server claim
          set({
            [isPremium ? 'claimedPremiumRewards' : 'claimedFreeRewards']: [
              ...(isPremium ? state.claimedPremiumRewards : state.claimedFreeRewards),
              level,
            ],
          });
          
          return reward;
        } catch (error) {
          console.error('Failed to claim reward:', error);
          return null;
        }
      },
      
      /**
       * @deprecated Compra avulsa removida. Use assinatura (Bronze/Silver/Gold) para ativar.
       * Mantido para compatibilidade, mas não faz mais nada.
       */
      purchasePremium: () => {
        // Compra avulsa removida - Battle Pass só é ativado via assinatura
        console.warn('[BattlePass] purchasePremium() deprecado. Use uma assinatura para ativar o Passe Premium.');
        return false;
      },
      
      /**
       * Ativa o Passe Premium via assinatura (Bronze/Silver/Gold).
       * Chamado automaticamente quando o usuário tem uma assinatura que inclui o Battle Pass.
       * Todas as assinaturas pagas incluem o Battle Pass Premium.
       */
      activatePremiumFromSubscription: () => {
        set({ hasPremium: true });
      },
      
      purchaseLevels: (count: number) => {
        const state = get();
        const { currentLevel, currentSeason } = state;
        
        if (currentLevel + count > currentSeason.maxLevel) {
          return false;
        }
        
        // 150 crystals por nível
        const costPerLevel = 150;
        const totalCost = costPerLevel * count;
        const canAfford = useProgress.getState().spendCrystals(totalCost);
        
        if (!canAfford) return false;
        
        set({ currentLevel: currentLevel + count, currentXP: 0 });
        return true;
      },
      
      refreshDailyMissions: async () => {
        const odysId = getPlayerId();
        
        try {
          const response = await apiFetch(`/api/battle-pass/missions/${odysId}`);
          const data = await response.json();
          
          if (data.success && data.dailyMissions) {
            set({
              dailyMissions: data.dailyMissions,
              lastDailyRefresh: data.lastDailyRefresh || null,
            });
          }
        } catch (error) {
          console.error('Failed to refresh daily missions:', error);
          // Fallback to local generation if server fails
          const today = getDateString();
          const { lastDailyRefresh } = get();
          
          if (lastDailyRefresh === today) return;
          
          const shuffled = [...DAILY_MISSION_TEMPLATES].sort(() => Math.random() - 0.5);
          const selected = shuffled.slice(0, 3);
          
          const missions: BattlePassMission[] = selected.map((template, index) => ({
            id: `daily_${today}_${index}`,
            type: 'daily',
            title: template.title,
            description: template.description,
            xpReward: template.xp,
            progress: 0,
            target: template.target,
            completed: false,
            claimed: false,
            expiresAt: new Date(today).getTime() + 24 * 60 * 60 * 1000,
          }));
          
          set({ dailyMissions: missions, lastDailyRefresh: today });
        }
      },
      
      refreshWeeklyMissions: async () => {
        const odysId = getPlayerId();
        
        try {
          const response = await apiFetch(`/api/battle-pass/missions/${odysId}`);
          const data = await response.json();
          
          if (data.success && data.weeklyMissions) {
            set({
              weeklyMissions: data.weeklyMissions,
              lastWeeklyRefresh: data.lastWeeklyRefresh || null,
            });
          }
        } catch (error) {
          console.error('Failed to refresh weekly missions:', error);
          // Fallback to local generation if server fails
          const thisWeek = getWeekString();
          const { lastWeeklyRefresh } = get();
          
          if (lastWeeklyRefresh === thisWeek) return;
          
          const shuffled = [...WEEKLY_MISSION_TEMPLATES].sort(() => Math.random() - 0.5);
          const selected = shuffled.slice(0, 3);
          
          const missions: BattlePassMission[] = selected.map((template, index) => ({
            id: `weekly_${thisWeek}_${index}`,
            type: 'weekly',
            title: template.title,
            description: template.description,
            xpReward: template.xp,
            progress: 0,
            target: template.target,
            completed: false,
            claimed: false,
            expiresAt: Date.now() + 7 * 24 * 60 * 60 * 1000,
          }));
          
          set({ weeklyMissions: missions, lastWeeklyRefresh: thisWeek });
        }
      },
      
      updateMissionProgress: (missionId: string, progress: number) => {
        const state = get();
        
        const updateMissions = (missions: BattlePassMission[]): BattlePassMission[] => {
          return missions.map(m => {
            if (m.id !== missionId || m.completed) return m;
            
            const newProgress = Math.min(m.target, progress);
            const completed = newProgress >= m.target;
            
            return { ...m, progress: newProgress, completed };
          });
        };
        
        set({
          dailyMissions: updateMissions(state.dailyMissions),
          weeklyMissions: updateMissions(state.weeklyMissions),
        });
      },
      
      claimMissionReward: async (missionId: string) => {
        const state = get();
        const odysId = getPlayerId();
        
        const findAndClaim = (missions: BattlePassMission[]): { updated: BattlePassMission[]; xp: number } => {
          let xpReward = 0;
          const updated = missions.map(m => {
            if (m.id === missionId && m.completed && !m.claimed) {
              xpReward = m.xpReward;
              return { ...m, claimed: true };
            }
            return m;
          });
          return { updated, xp: xpReward };
        };
        
        const dailyResult = findAndClaim(state.dailyMissions);
        const weeklyResult = findAndClaim(state.weeklyMissions);
        
        const totalXP = dailyResult.xp + weeklyResult.xp;
        
        if (totalXP > 0) {
          // Update local state
          set({
            dailyMissions: dailyResult.updated,
            weeklyMissions: weeklyResult.updated,
            totalMissionsCompleted: state.totalMissionsCompleted + 1,
          });
          
          // Call server to claim mission - server will handle XP addition
          // We don't call addXP here to avoid double-counting
          try {
            const response = await apiFetch('/api/battle-pass/claim-mission', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ odysId, missionId, xpAmount: totalXP }),
            });
            
            const data = await response.json();
            
            // Update progress from server response if available
            if (data.success && data.progress) {
              set({
                currentLevel: data.progress.currentLevel,
                currentXP: data.progress.currentXp,
              });
            }
          } catch (error) {
            console.error('Failed to claim mission on server:', error);
            // Fallback: add XP locally if server fails
            await state.addXP(totalXP);
          }
        }
        
        return totalXP;
      },
      
      canClaimReward: (level: number, isPremium: boolean) => {
        const state = get();
        
        // Verificar se já passou do nível
        if (level > state.currentLevel) return false;
        
        // Verificar se já coletou
        const claimed = isPremium ? state.claimedPremiumRewards : state.claimedFreeRewards;
        if (claimed.includes(level)) return false;
        
        // Verificar se tem premium para recompensas premium
        if (isPremium && !state.hasPremium) return false;
        
        // Verificar se existe recompensa
        const levelConfig = state.currentSeason.levels[level - 1];
        if (!levelConfig) return false;
        const reward = isPremium ? levelConfig.premiumReward : levelConfig.freeReward;
        
        return !!reward;
      },
      
      getTimeRemaining: () => {
        const { currentSeason } = get();
        const remaining = currentSeason.endDate - Date.now();
        
        if (remaining <= 0) {
          return { days: 0, hours: 0, minutes: 0 };
        }
        
        const days = Math.floor(remaining / (24 * 60 * 60 * 1000));
        const hours = Math.floor((remaining % (24 * 60 * 60 * 1000)) / (60 * 60 * 1000));
        const minutes = Math.floor((remaining % (60 * 60 * 1000)) / (60 * 1000));
        
        return { days, hours, minutes };
      },
      
      getLevelProgress: () => {
        const { currentXP, currentLevel, currentSeason } = get();
        
        if (currentLevel >= currentSeason.maxLevel) {
          return { current: 0, required: 0, percentage: 100 };
        }
        
        const levelConfig = currentSeason.levels[currentLevel - 1];
        if (!levelConfig || !isFiniteNumber(levelConfig.xpRequired) || levelConfig.xpRequired <= 0) {
          return { current: 0, required: 0, percentage: 0 };
        }
        const percentage = (currentXP / levelConfig.xpRequired) * 100;
        
        return {
          current: currentXP,
          required: levelConfig.xpRequired,
          percentage: Math.min(100, percentage),
        };
      },
    }),
    {
      name: 'rocketanks-battle-pass',
      merge: (persistedState, currentState) => normalizeBattlePassState(persistedState, currentState),
    }
  )
);

// =====================================================
// SUBSCRIPTION STORE (STRIPE INTEGRATION)
// =====================================================

interface SubscriptionState {
  // Estado da assinatura
  subscription: ActiveSubscription | null;
  serverBenefits: SubscriptionPlan['benefits'] | null;
  plans: SubscriptionPlan[];
  preferredCurrency: SubscriptionCurrency;
  isLoading: boolean;
  error: string | null;
  
  // Ações
  loadPlans: (force?: boolean) => Promise<void>;
  loadSubscription: () => Promise<void>;
  subscribe: (tier: SubscriptionTier) => Promise<{ url: string } | null>;
  pollSubscriptionStatus: (maxAttempts?: number, intervalMs?: number) => Promise<boolean>;
  cancelSubscription: () => Promise<boolean>;
  reactivateSubscription: () => Promise<boolean>;
  claimDailyRewards: () => Promise<{ crystals: number; chips: number } | null>;
  openCustomerPortal: () => Promise<{ url: string } | null>;
  
  // Helpers
  canClaimDailyRewards: () => boolean;
  getCurrentPlan: () => SubscriptionPlan;
  getAvailablePlans: () => SubscriptionPlan[];
  getDaysRemaining: () => number;
  getXPMultiplier: () => number;
  getChipMultiplier: () => number;
}

export const useSubscription = create<SubscriptionState>()(
  persist(
    (set, get) => ({
      subscription: null,
      serverBenefits: null,
      plans: [],
      preferredCurrency: 'brl',
      isLoading: false,
      error: null,

      loadPlans: async (force = false) => {
        const state = get();
        const hasServerCatalog =
          state.plans.length > 0 &&
          state.plans.some((plan) => Boolean(plan.primaryPrice) || Boolean(plan.prices && Object.keys(plan.prices).length > 0));
        if (!force && hasServerCatalog) return;

        try {
          const locale = useI18n.getState().locale;
          const localeCurrency: SubscriptionCurrency =
            locale === 'pt-BR' ? 'brl' : locale === 'ja' ? 'jpy' : 'usd';

          const response = await apiFetch(`/api/subscription/plans?locale=${encodeURIComponent(locale)}&currency=${localeCurrency}`);
          const data = await response.json();

          if (response.ok && data?.success) {
            const available = mergeSubscriptionPlansFromServer(data.plans);
            set({
              plans: available,
              preferredCurrency: (normalizeSubscriptionCurrency(data.currency) || localeCurrency),
            });
          } else if (!force) {
            const fallback = DEFAULT_SUBSCRIPTION_ORDER
              .map((tier) => SUBSCRIPTION_PLANS[tier])
              .filter((plan): plan is SubscriptionPlan => Boolean(plan));
            set({ plans: fallback });
          }
        } catch {
          if (!force) {
            const fallback = DEFAULT_SUBSCRIPTION_ORDER
              .map((tier) => SUBSCRIPTION_PLANS[tier])
              .filter((plan): plan is SubscriptionPlan => Boolean(plan));
            set({ plans: fallback });
          }
        }
      },
      
      loadSubscription: async () => {
        set({ isLoading: true, error: null });
        const odysId = getPlayerId();
        
        try {
          await get().loadPlans(false);
          const response = await apiFetch(`/api/subscription/status?odysId=${odysId}`);
          const data = await response.json();
          
          if (data.active && data.subscription) {
            set({ subscription: data.subscription, isLoading: false });
            
            // Fetch server-validated benefits to prevent localStorage manipulation
            try {
              const benefitsRes = await apiFetch(`/api/subscription/benefits?odysId=${odysId}`);
              const benefitsData = await benefitsRes.json();
              if (benefitsData.benefits) {
                set({ serverBenefits: benefitsData.benefits });
              }
            } catch {
              // Non-critical: fall back to local SUBSCRIPTION_PLANS
            }
            
            // Ativa premium do Battle Pass se o plano server-side inclui esse benefício.
            const planBenefits = data.plan?.benefits || null;
            if (planBenefits?.battlePassIncluded) {
              useBattlePass.getState().activatePremiumFromSubscription();
            } else {
              const tier: SubscriptionTier = data.subscription.tier || 'free';
              if (tier !== 'free' && (SUBSCRIPTION_PLANS[tier]?.benefits?.battlePassIncluded || false)) {
                useBattlePass.getState().activatePremiumFromSubscription();
              }
            }
          } else if (data.active === false) {
            // Servidor confirmou: sem assinatura ativa
            set({ subscription: null, serverBenefits: null, isLoading: false });
          }
        } catch (error) {
          set({ 
            error: error instanceof Error ? error.message : 'Erro ao carregar assinatura',
            isLoading: false 
          });
        }
      },
      
      subscribe: async (tier: SubscriptionTier) => {
        const plan = SUBSCRIPTION_PLANS[tier];
        if (!plan || !plan.stripePriceId) return null;
        
        set({ isLoading: true, error: null });
        const odysId = getPlayerId();
        const locale = useI18n.getState().locale;
        const currency = get().preferredCurrency;
        
        try {
          const response = await apiFetch('/api/subscription/create', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              odysId,
              tier,
              locale,
              currency,
              platform: getClientPlatform(),
            }),
          });
          
          const data = await response.json();
          
          if (response.status === 503) {
            // Stripe not configured - show friendly message via error state
            const errorMsg = '⚠️ Sistema de pagamento não configurado.\n\nO Stripe ainda não está ativo neste ambiente.\n\nEm produção, você será redirecionado para a página de pagamento seguro.';
            set({ 
              error: errorMsg,
              isLoading: false 
            });
            return null;
          }
          
          if (data.checkoutUrl) {
            set({ isLoading: false });
            return { url: data.checkoutUrl };
          } else {
            const errorMsg = data.message || data.error || 'Servidor não retornou URL de checkout';
            set({ error: errorMsg, isLoading: false });
            return null;
          }
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Erro ao criar assinatura';
          set({ 
            error: errorMessage,
            isLoading: false 
          });
          return null;
        }
      },
      
      pollSubscriptionStatus: async (maxAttempts = 10, intervalMs = 2000) => {
        const odysId = getPlayerId();
        for (let i = 0; i < maxAttempts; i++) {
          try {
            const response = await apiFetch(`/api/subscription/status?odysId=${odysId}`);
            const data = await response.json();
            if (data.active && data.subscription) {
              set({ subscription: data.subscription });
              
              // Ativa premium do Battle Pass se o plano inclui esse benefício
              const planBenefits = data.plan?.benefits || null;
              if (planBenefits?.battlePassIncluded) {
                useBattlePass.getState().activatePremiumFromSubscription();
              }
              return true;
            }
          } catch (error) {
            console.error('Erro ao verificar status da assinatura:', error);
          }
          await new Promise(resolve => setTimeout(resolve, intervalMs));
        }
        return false;
      },
      
      cancelSubscription: async () => {
        const { subscription } = get();
        if (!subscription) return false;
        
        set({ isLoading: true });
        const odysId = getPlayerId();
        
        try {
          const response = await apiFetch('/api/subscription/cancel', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              odysId,
            }),
          });
          
          const data = await response.json();
          
          if (data.success) {
            set(state => ({
              subscription: state.subscription ? {
                ...state.subscription,
                cancelAtPeriodEnd: true,
              } : null,
              isLoading: false,
            }));
            return true;
          }
          return false;
        } catch (error) {
          set({ isLoading: false });
          return false;
        }
      },
      
      reactivateSubscription: async () => {
        const { subscription } = get();
        if (!subscription) return false;
        
        set({ isLoading: true });
        const odysId = getPlayerId();
        
        try {
          const response = await apiFetch('/api/subscription/reactivate', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              odysId,
            }),
          });
          
          const data = await response.json();
          
          if (data.success) {
            set(state => ({
              subscription: state.subscription ? {
                ...state.subscription,
                cancelAtPeriodEnd: false,
              } : null,
              isLoading: false,
            }));
            return true;
          }
          return false;
        } catch (error) {
          set({ isLoading: false });
          return false;
        }
      },
      
      // Abrir portal do cliente Stripe para gerenciar assinatura
      openCustomerPortal: async () => {
        set({ isLoading: true, error: null });
        const odysId = getPlayerId();
        
        try {
          const response = await apiFetch('/api/subscription/portal', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ odysId, platform: getClientPlatform() }),
          });
          
          const data = await response.json();
          
          if (data.url) {
            set({ isLoading: false });
            return { url: data.url };
          } else {
            throw new Error(data.error || 'Erro ao abrir portal');
          }
        } catch (error) {
          set({ 
            error: error instanceof Error ? error.message : 'Erro ao abrir portal',
            isLoading: false 
          });
          return null;
        }
      },
      
      claimDailyRewards: async () => {
        const state = get();
        if (!state.canClaimDailyRewards()) return null;
        
        const today = new Date().toISOString().split('T')[0];
        const odysId = getPlayerId();
        
        try {
          const response = await apiFetch('/api/subscription/claim-daily', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              odysId,
            }),
          });
          
          const data = await response.json();
          
          if (data.success) {
            // Usar valores retornados pelo SERVIDOR (não locais!)
            const serverCrystals = data.rewards?.crystals ?? 0;
            const serverChips = data.rewards?.chips ?? 0;
            
            set(state => ({
              subscription: state.subscription ? {
                ...state.subscription,
                lastDailyClaimDate: today,
                totalCrystalsReceived: data.totalReceived?.crystals ?? (state.subscription.totalCrystalsReceived + serverCrystals),
                totalChipsReceived: data.totalReceived?.chips ?? (state.subscription.totalChipsReceived + serverChips),
              } : null,
            }));
            
            return {
              crystals: serverCrystals,
              chips: serverChips,
            };
          }
          return null;
        } catch (error) {
          return null;
        }
      },
      
      canClaimDailyRewards: () => {
        const { subscription } = get();
        if (!subscription) return false;
        const normalized = String(subscription.status || '').toLowerCase();
        if (normalized !== 'active' && normalized !== 'trialing') return false;
        
        const today = new Date().toISOString().split('T')[0];
        return subscription.lastDailyClaimDate !== today;
      },
      
      getCurrentPlan: () => {
        const { subscription, serverBenefits } = get();
        if (!subscription) return SUBSCRIPTION_PLANS.free;
        const plan = SUBSCRIPTION_PLANS[subscription.tier] ?? {
          ...SUBSCRIPTION_PLANS.free,
          tier: subscription.tier,
          name: subscription.tier,
          displayName: subscription.tier,
        };
        // Prefer server-validated benefits when available
        if (serverBenefits) {
          return { ...plan, benefits: serverBenefits };
        }
        return plan;
      },

      getAvailablePlans: () => {
        const { plans } = get();
        if (plans.length > 0) return plans;
        return DEFAULT_SUBSCRIPTION_ORDER
          .map((tier) => SUBSCRIPTION_PLANS[tier])
          .filter((plan): plan is SubscriptionPlan => Boolean(plan));
      },
      
      getDaysRemaining: () => {
        const { subscription } = get();
        if (!subscription) return 0;
        
        const remaining = subscription.currentPeriodEnd - Date.now();
        return Math.max(0, Math.ceil(remaining / (24 * 60 * 60 * 1000)));
      },
      
      getXPMultiplier: () => {
        return get().getCurrentPlan().benefits.xpMultiplier;
      },
      
      getChipMultiplier: () => {
        return get().getCurrentPlan().benefits.chipMultiplier;
      },
    }),
    {
      name: 'rocketanks-subscription',
      merge: (persistedState, currentState) => {
        if (!persistedState || typeof persistedState !== 'object') return currentState;
        const source = persistedState as Partial<SubscriptionState>;
        
        // Validate subscription tier if present
        let validatedSubscription = source.subscription;
        if (validatedSubscription && typeof validatedSubscription === 'object') {
          const tier = validatedSubscription.tier as SubscriptionTier;
          // If tier is invalid string, reset to null
          if (!tier || typeof tier !== 'string') {
            validatedSubscription = null;
          }
        }
        
        return {
          ...currentState,
          subscription: validatedSubscription ?? currentState.subscription,
          serverBenefits: source.serverBenefits ?? currentState.serverBenefits,
          plans: Array.isArray(source.plans) && source.plans.length > 0 ? source.plans : currentState.plans,
          preferredCurrency: normalizeSubscriptionCurrency(source.preferredCurrency) ?? currentState.preferredCurrency,
          isLoading: false, // Always start with loading false
          error: null, // Don't persist errors
        };
      },
    }
  )
);

// Hook com helpers de UI
export const useBattlePassUI = () => {
  const store = useBattlePass();
  
  const getRewardIcon = (type: BattlePassRewardType): string => {
    switch (type) {
      case 'chips': return '🪙';
      case 'crystals': return '💎';
      case 'skin': return '🎨';
      case 'tank': return '🚀';
      case 'emote': return '😎';
      case 'spray': return '🖼️';
      case 'frame': return '🖼️';
      case 'explosion_effect': return '💥';
      case 'trail_effect': return '✨';
      case 'xp_boost': return '⚡';
      default: return '❓';
    }
  };
  
  const getRarityColor = (rarity: BattlePassReward['rarity']): string => {
    switch (rarity) {
      case 'common': return 'text-gray-400';
      case 'rare': return 'text-blue-400';
      case 'epic': return 'text-purple-400';
      case 'legendary': return 'text-yellow-400';
      default: return 'text-gray-400';
    }
  };
  
  const getRarityBg = (rarity: BattlePassReward['rarity']): string => {
    switch (rarity) {
      case 'common': return 'bg-gray-800/50';
      case 'rare': return 'bg-blue-900/50';
      case 'epic': return 'bg-purple-900/50';
      case 'legendary': return 'bg-yellow-900/50 animate-pulse';
      default: return 'bg-gray-800/50';
    }
  };
  
  return {
    ...store,
    getRewardIcon,
    getRarityColor,
    getRarityBg,
  };
};

// Hook para subscription UI
export const useSubscriptionUI = () => {
  const store = useSubscription();
  
  const getTierIcon = (tier: SubscriptionTier): string => {
    switch (tier) {
      case 'free': return '🆓';
      case 'bronze': return '🥉';
      case 'silver': return '🥈';
      case 'gold': return '🥇';
      default: return '❓';
    }
  };
  
  const getTierColor = (tier: SubscriptionTier): string => {
    switch (tier) {
      case 'free': return 'text-gray-400';
      case 'bronze': return 'text-amber-600';
      case 'silver': return 'text-gray-300';
      case 'gold': return 'text-yellow-400';
      default: return 'text-gray-400';
    }
  };
  
  const getTierGradient = (tier: SubscriptionTier): string => {
    switch (tier) {
      case 'free': return 'from-gray-600 to-gray-700';
      case 'bronze': return 'from-amber-700 to-orange-800';
      case 'silver': return 'from-gray-400 to-gray-500';
      case 'gold': return 'from-yellow-500 to-amber-600';
      default: return 'from-gray-600 to-gray-700';
    }
  };
  
  return {
    ...store,
    getTierIcon,
    getTierColor,
    getTierGradient,
  };
};
